import { useState } from "react";
import { User as UserModel } from "../../../models/user";
import ProfileCard from "./ProfileCard";
import EditProfile from "./EditProfile";
import CollectibleSelector from "./CollectibleSelector";
import { SplashScreen } from "../../../models/splashScreens";
import { CardTheme } from "../../../models/cardThemes";
import { ProfileImage } from "../../../models/profileImages";
import styles from "../ProfilePage.module.scss";
import { useTranslation } from "react-i18next";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

interface ProfileProps {
  user: UserModel;
  rank: number;
  badgeCount: number;
  collectibleCount: number;
  profileImages: ProfileImage[];
  splashScreens: SplashScreen[];
  cardThemes: CardTheme[];
  onProfileSaved: (user: UserModel) => void;
  className?: string;
}

const Profile = ({
  user,
  rank,
  badgeCount,
  collectibleCount,
  profileImages,
  splashScreens,
  cardThemes,
  onProfileSaved,
  className,
}: ProfileProps) => {
  const { t } = useTranslation();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showCollectibles, setShowCollectibles] = useState(false);

  return (
    <div className={`page-content-container`}>
      <ProfileCard
        user={user}
        rank={rank}
        badgeCount={badgeCount}
        collectibleCount={collectibleCount}
      />

      {/* Edit Profile Section */}
      <section className={styles.section}>
        <button
          className={styles.toggleButton}
          onClick={() => setShowEditProfile(!showEditProfile)}
        >
          <span>{t("profile.editProfile")}</span>
          <span className={styles.arrow}>
            {showEditProfile ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </button>
        {showEditProfile && (
          <div className={styles.sectionContent}>
            <EditProfile user={user} onProfileSaved={onProfileSaved} />
          </div>
        )}
      </section>

      {/* Collectibles Section */}
      <section className={styles.section}>
        <button
          className={styles.toggleButton}
          onClick={() => setShowCollectibles(!showCollectibles)}
        >
          <span>{t("profile.selectCollectible")}</span>
          <span className={styles.arrow}>
            {showCollectibles ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </button>
        {showCollectibles && (
          <div
            className={`${styles.sectionContent} ${styles.collectiblesGrid}`}
          >
            <div className={styles.collectibleItem}>
              <h3 className={styles.collectibleTitle}>Profile Images</h3>
              <CollectibleSelector
                user={user}
                activeCollectible={user.activeProfileImage}
                collectibles={profileImages}
                collectableType="profileImages"
                onImageChange={onProfileSaved}
              />
            </div>
            <div className={styles.collectibleItem}>
              <h3 className={styles.collectibleTitle}>Splash Screens</h3>
              <CollectibleSelector
                user={user}
                activeCollectible={user.activeSplashScreen}
                collectibles={splashScreens}
                collectableType="splashScreens"
                onImageChange={onProfileSaved}
              />
            </div>
            <div className={styles.collectibleItem}>
              <h3 className={styles.collectibleTitle}>Card Themes</h3>
              <CollectibleSelector
                user={user}
                activeCollectible={user.activeCardTheme}
                collectibles={cardThemes}
                collectableType="cardThemes"
                onImageChange={onProfileSaved}
              />
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Profile;
